import { useCallback, useMemo, useSyncExternalStore } from "react";
import {
  adjustRest,
  decodeRest,
  endRest,
  freshRestSnapshot,
  restSnapshot,
  restTaken,
  saveRest,
  subscribeRest,
} from "../lib/rest.ts";

/**
 * The rest countdown of a session, kept in storage and shared: the
 * movement's screen and the bar over every other screen read the same one.
 */
export function useTimer(sessionId: number) {
  // A stale rest is dropped where the store is read, not while rendering.
  const raw = useSyncExternalStore(subscribeRest, () => freshRestSnapshot(sessionId));
  const rest = useMemo(() => decodeRest(raw), [raw]);

  const start = useCallback(
    (duration: number) => saveRest(sessionId, { startedAt: Date.now(), seconds: duration }),
    [sessionId],
  );

  // Ended, not forgotten: the next set reads how long it lasted.
  const stop = useCallback(() => {
    const current = decodeRest(restSnapshot(sessionId));
    if (current && current.endedAt === undefined) saveRest(sessionId, endRest(current, Date.now()));
  }, [sessionId]);

  /** The rest really taken before a set validated now, itself lasting about `setSeconds`. */
  const taken = useCallback(
    (setSeconds: number) => restTaken(decodeRest(restSnapshot(sessionId)), Date.now(), setSeconds),
    [sessionId],
  );

  const adjust = useCallback(
    (delta: number) => {
      if (rest) saveRest(sessionId, adjustRest(rest, delta));
    },
    [rest, sessionId],
  );

  return { startedAt: rest?.startedAt ?? null, seconds: rest?.seconds ?? 30, start, stop, adjust, taken };
}
